import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import {
  MzicSvgComponent,
  SelectBasicComponent,
  SpinnerComponent,
} from '@mzic/mzic-components';
import { SelectOption, SelectOptionData } from '@mzic/mzic-interfaces';
import { CurrencyEnum } from '@mzic/mzic-utils';
import {
  CollectionRequiredInputDescriptor,
  isStatementOriginRequiredInput,
  isSupportedRequiredInputDescriptor,
} from '../../collection-flow/collection-revision-file-editor';
import {
  CollectionValidationRequiredInput,
  SOURCE_STATEMENT_CURRENCY_INPUT_KEY,
  STATEMENT_ORIGIN_INPUT_KEY,
  StatementOriginRequiredInput,
} from '../../interfaces/collection-revision.interface';
import { StatementOriginRow } from '../../interfaces/statement-origin.interface';
import { CollectionReportFileCardComponent } from '../collection-report-file-card/collection-report-file-card.component';
import { StatementOriginPickerComponent } from '../statement-origin-picker/statement-origin-picker.component';

export interface FileRequiredInputsReviewItem {
  readonly fileId: string;
  readonly fileName: string;
  readonly sizeBytes: number | null;
  readonly requiredInputs: readonly CollectionRequiredInputDescriptor[];
  readonly resolvedInputs: Readonly<Record<string, string>>;
}

export interface FileRequiredInputsSubmission {
  readonly fileId: string;
  readonly resolvedInputs: Readonly<Record<string, string>>;
}

@Component({
  selector: 'lib-file-required-inputs-dialog',
  imports: [
    CommonModule,
    A11yModule,
    TranslocoModule,
    MzicSvgComponent,
    SelectBasicComponent,
    SpinnerComponent,
    CollectionReportFileCardComponent,
    StatementOriginPickerComponent,
  ],
  providers: [
    provideTranslocoScope({
      scope: 'mzic-collection-flow',
      alias: 'lang',
    }),
  ],
  templateUrl: './file-required-inputs-dialog.component.html',
  styleUrl: './file-required-inputs-dialog.component.scss',
})
export class FileRequiredInputsDialogComponent implements OnChanges {
  private static readonly CURRENCY_CODES = new Set<string>(
    Object.values(CurrencyEnum),
  );

  @Input({ required: true }) file!: FileRequiredInputsReviewItem;
  @Input({ required: true }) collectionId!: string;
  @Input({ required: true }) revisionId!: string;
  @Input() current = 1;
  @Input() total = 1;
  @Input() busy = false;
  @Input() errorMessage = '';
  @Output() readonly submitRequiredInputs =
    new EventEmitter<FileRequiredInputsSubmission>();
  @Output() readonly closeRequested = new EventEmitter<void>();

  readonly currencyOptions: SelectOption[] = Object.values(CurrencyEnum).map(
    (currency) => ({ id: currency, info: { title: currency } }),
  );
  readonly draftInputs: Record<string, string> = {};
  readonly titleId = 'file-required-inputs-title';
  readonly descriptionId = 'file-required-inputs-description';

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['file'] || !this.file) return;
    for (const key of Object.keys(this.draftInputs)) {
      delete this.draftInputs[key];
    }
    Object.assign(this.draftInputs, this.file.resolvedInputs);
  }

  get progressLabelParams(): { current: number; total: number } {
    return { current: this.current, total: this.total };
  }

  get progressPercent(): number {
    return this.total <= 0 ? 0 : (this.current / this.total) * 100;
  }

  get canSubmit(): boolean {
    return (
      !this.busy &&
      this.file.requiredInputs.length > 0 &&
      this.file.requiredInputs.every(
        (descriptor) =>
          this.isSupportedRequiredInput(descriptor) &&
          Boolean(this.draftInputs[descriptor.key]?.trim()),
      )
    );
  }

  isSupportedRequiredInput(
    descriptor: CollectionRequiredInputDescriptor,
  ): descriptor is CollectionValidationRequiredInput {
    return isSupportedRequiredInputDescriptor(descriptor);
  }

  isSourceCurrencyInput(
    descriptor: CollectionRequiredInputDescriptor,
  ): boolean {
    return (
      this.isSupportedRequiredInput(descriptor) &&
      descriptor.key === SOURCE_STATEMENT_CURRENCY_INPUT_KEY
    );
  }

  /**
   * Returns the descriptor only when it is a supported statement-origin
   * requirement, so a malformed origin descriptor falls through to the
   * "unsupported input" alert instead of rendering a picker.
   */
  statementOriginInput(
    descriptor: CollectionRequiredInputDescriptor,
  ): StatementOriginRequiredInput | null {
    return isStatementOriginRequiredInput(descriptor) ? descriptor : null;
  }

  get selectedStatementOriginId(): string | null {
    return this.draftInputs[STATEMENT_ORIGIN_INPUT_KEY] ?? null;
  }

  selectedSourceCurrency(): SelectOptionData[] {
    const currency = this.draftInputs[SOURCE_STATEMENT_CURRENCY_INPUT_KEY];
    const option = this.currencyOptions.find(
      (candidate) => candidate.id === currency,
    );
    return option ? [option.info] : [];
  }

  resolveSourceCurrency(selected: (string | number)[] | undefined): void {
    const currency = selected?.[0]?.toString().toUpperCase();
    if (
      !currency ||
      !FileRequiredInputsDialogComponent.CURRENCY_CODES.has(currency)
    ) {
      delete this.draftInputs[SOURCE_STATEMENT_CURRENCY_INPUT_KEY];
      return;
    }
    this.draftInputs[SOURCE_STATEMENT_CURRENCY_INPUT_KEY] = currency;
  }

  /** The catalog id is stored exactly as the catalog produced it (D-07). */
  resolveStatementOrigin(row: StatementOriginRow | null): void {
    if (!row || !row.id) {
      delete this.draftInputs[STATEMENT_ORIGIN_INPUT_KEY];
      return;
    }
    this.draftInputs[STATEMENT_ORIGIN_INPUT_KEY] = row.id;
  }

  onSubmit(): void {
    if (!this.canSubmit) return;
    this.submitRequiredInputs.emit({
      fileId: this.file.fileId,
      resolvedInputs: Object.freeze({ ...this.draftInputs }),
    });
  }

  onClose(): void {
    if (!this.busy) this.closeRequested.emit();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) this.onClose();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscape(event: KeyboardEvent): void {
    if (this.busy) return;
    event.preventDefault();
    this.onClose();
  }
}
